import { activatePhotos } from "./shared/photos";
import { requireElement } from "./shared/dom";

// Keep the prepared one-minute clip playing whenever the browser permits audio.
class WeddingMusic {
  private audio: HTMLAudioElement | null = null;
  private starting: Promise<void> | null = null;

  get playing(): boolean { return this.audio !== null && !this.audio.paused; }

  async play(): Promise<void> {
    if (!this.audio) {
      this.audio = new Audio();
      this.audio.preload = "none";
      this.audio.loop = true;
      this.audio.volume = .25;
      this.audio.hidden = true;
      this.audio.src = "assets/audio/only-you-loop.mp3";
      document.body.append(this.audio);
    }
    if (this.audio.error) this.audio.load();
    const starting = this.starting ?? this.audio.play();
    this.starting = starting;
    try { await starting; }
    finally { if (this.starting === starting) this.starting = null; }
  }

  async toggle(): Promise<boolean> {
    if (this.playing) { await this.pause(); return false; }
    await this.play();
    return true;
  }

  async pause(): Promise<void> { this.audio?.pause(); }
}

export function startInvitation(): void {
  const dialog = document.createElement("dialog");
  dialog.className = "invitation-cover";
  dialog.setAttribute("aria-label", "Naanep and Emmanuel's wedding invitation");
  dialog.innerHTML = `<div class="letter-layout">
    <div class="letter-top"><span class="letter-monogram">N &amp; E</span><button id="skip-invitation" type="button">RSVP ↗</button></div>
    <div class="letter-main">
      <div class="letter-intro"><p class="eyebrow">Together with their families</p><h1>Naanep <em>&amp;</em> Emmanuel</h1><div class="letter-date"><span>05</span><span>December 2026<br />Abuja, Nigeria</span></div></div>
      <div class="letter-stage">
        <div class="envelope">
          <div class="envelope-back" aria-hidden="true"></div>
          <div class="envelope-letter" aria-hidden="true"><small>You are warmly invited</small><strong>Naanep &amp; Emmanuel</strong><span>05 · 12 · 2026</span></div>
          <div class="envelope-pocket" aria-hidden="true"></div><div class="envelope-folds" aria-hidden="true"></div><div class="envelope-flap" aria-hidden="true"></div>
          <button class="wax-seal" type="button" aria-label="Break the seal and open invitation">N·E</button>
        </div>
        <p class="sr-only" id="opening-status" aria-live="polite"></p>
        <button class="letter-open" id="open-invitation" type="button">Open invitation ↗</button>
      </div>
    </div>
  </div>`;
  document.body.append(dialog);
  const music = new WeddingMusic();
  const toggle = document.createElement("button");
  toggle.type = "button"; toggle.className = "music-toggle";
  toggle.textContent = "Play music"; toggle.setAttribute("aria-pressed", "false");
  document.body.append(toggle);
  let musicEnabled = false;
  const showMusicState = (playing: boolean) => {
    toggle.textContent = playing ? "Pause music" : "Play music";
    toggle.setAttribute("aria-pressed", String(playing));
  };
  const startMusic = async (): Promise<boolean> => {
    if (!musicEnabled || music.playing) return music.playing;
    try {
      await music.play();
      showMusicState(true);
      return true;
    } catch {
      showMusicState(false);
      return false;
    }
  };
  toggle.addEventListener("click", async () => {
    toggle.disabled = true;
    try {
      const playing = await music.toggle();
      musicEnabled = playing;
      showMusicState(playing);
    } catch {
      musicEnabled = false;
      toggle.textContent = "Music unavailable";
      toggle.setAttribute("aria-pressed", "false");
    }
    finally { toggle.disabled = false; }
  });
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void startMusic();
  });
  let opening = false;
  let closeTimer: ReturnType<typeof setTimeout> | undefined;
  let fadeTimer: ReturnType<typeof setTimeout> | undefined;
  let photosActivated = false;
  let target = "#hero-title";
  const activate = () => {
    if (photosActivated) return;
    photosActivated = true;
    activatePhotos();
  };
  const finish = () => dialog.close();
  const open = () => {
    if (opening) return;
    opening = true;
    musicEnabled = true;
    void startMusic();
    activate();
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return finish();
    requireElement<HTMLElement>(".letter-stage", dialog).classList.add("is-opening");
    requireElement<HTMLElement>("#opening-status", dialog).textContent = "Opening invitation…";
    requireElement<HTMLButtonElement>("#open-invitation", dialog).disabled = true;
    requireElement<HTMLButtonElement>(".wax-seal", dialog).disabled = true;
    fadeTimer = setTimeout(() => dialog.classList.add("cover-leaving"), 1700);
    closeTimer = setTimeout(finish, 2150);
  };
  requireElement<HTMLButtonElement>("#open-invitation", dialog).addEventListener("click", open);
  requireElement<HTMLButtonElement>(".wax-seal", dialog).addEventListener("click", open);
  requireElement<HTMLButtonElement>("#skip-invitation", dialog).addEventListener("click", () => {
    target = "#rsvp-title"; finish();
  });
  dialog.addEventListener("close", () => {
    clearTimeout(closeTimer); clearTimeout(fadeTimer);
    document.body.classList.remove("invitation-open");
    activate();
    const heading = requireElement<HTMLElement>(target);
    heading.tabIndex = -1; heading.focus({ preventScroll: true });
    if (target === "#rsvp-title") {
      history.replaceState(null, "", "#rsvp");
      requireElement<HTMLElement>("#rsvp").scrollIntoView({ behavior: "instant" });
    }
  });
  const replay = document.createElement("button");
  replay.className = "reopen-letter"; replay.type = "button"; replay.textContent = "View invitation";
  document.querySelector(".footer-links")?.append(replay);
  replay.addEventListener("click", () => {
    opening = false; target = "#hero-title";
    dialog.classList.remove("cover-leaving");
    requireElement<HTMLElement>(".letter-stage", dialog).classList.remove("is-opening");
    requireElement<HTMLElement>("#opening-status", dialog).textContent = "";
    requireElement<HTMLButtonElement>("#open-invitation", dialog).disabled = false;
    requireElement<HTMLButtonElement>(".wax-seal", dialog).disabled = false;
    document.body.classList.add("invitation-open"); dialog.showModal();
  });
  document.body.classList.add("invitation-open");
  dialog.showModal();
}
